/**
 *  @file       raw_readln.js   
 *  @author     Wes Garland, wes@kingsds.network
 *  @date       April 2020
 */ 
"use strict";

let leftover = false;
var process;
const fsBasic = require('dcp/common/fs-basic');

if (require('dcp/common/dcp-env').platform === 'nodejs')
  process = require('dcp/dcp-client/webpack-native-bridge').requireNative('process');

const LANG = require('dcp/common/dcp-env').getenv('LANG');
if (LANG && LANG !== 'C' && !LANG.match(/UTF-?8/gi))
  console.log(`Warning: input language ${LANG} not supported; assuming UTF-8`);

/** Read a line of input from stdin, emulating standard UNIX terminal line 
 *  discipline. Input stream must be ASCII or UTF-8. This routine blocks the 
 *  event loop.
 *
 *  @param    {string}  echoChar    The charater to echo when a key is pressed.  When
 *                                  undefined, nothing is echoed. When true, the input
 *                                  character is (more or less) echoed back.  More or
 *                                  less because, rather than typical full-duplex terminal
 *                                  emulation that echoes the exact input, in our case
 *                                  the input is converted from UTF-8 to UTF-16 for output.
 *  @param    {number}  bufSize     An optional parameter to specify the buffer size. 
 *                                  A large (256-byte?) buffer will perform when when reading
 *                                  large amounts of data, however the 1-byte default
 *                                  will prevent this code from consuming extra data from
 *                                  the stdin stream.
 *
 *  @returns  {string}  normalized UTF-16 representation of input.  Newline is discarded. 
 *                      Invalid input could yield unpaired surrogates in the output.
 * 
 *  @note  Known Bugs - when using echoChar = true, there will be display bugs for 
 *         erase and kill when dealing with codepoints outside of the BMP, as well as
 *         unpaired surrogates.
 */
exports.readlnSync = function utils$$rawReadlnSync(echoChar, bufSize) {
  var byteBuf = Buffer.alloc(bufSize || 1); /* io buffer */
  var buf = '';                             /* accumulator */
  var ret;
  var initRawMode = process.stdin.isRaw;
  
  if (process.stdin.setRawMode && !process.stdin.isRaw)
    process.stdin.setRawMode(true);

  try {
    loop: while (true) {
      let nBytes;

      try {
        if (!leftover)
          nBytes = require('dcp/common/fs-basic').readSync(process.stdin.fd, byteBuf, 0, byteBuf.length);
        else {
          leftover.copy(byteBuf);
          nBytes = leftover.length;
          leftover = false;
        }
      } catch (e) {
        switch(e.code) {
        case 'EAGAIN':
          nBytes = 0
          break
        case 'EOF':
          nBytes = -1
          break
        default:
          throw e
        }
      }

      if (nBytes < 0) {
        buf = null; /* input stream is closed */
        break loop;
      }
      if (nBytes == 0) { /* nothing to read - give up timeslice */
        nap();
        continue;
      }
      ret = buildLine(buf, byteBuf, nBytes, echoChar);
      buf = ret.buf;
      if (ret.done)
        break loop;
    }

    if (process.stdin.setRawMode) {
      process.stdout.write(Buffer.from([13]));
      process.stdout.write(Buffer.from([10]));
    }
  } finally {
    if (typeof initRawMode === 'boolean')
      process.stdin.setRawMode(initRawMode);
  }

  return buf;
}

/** Same as readlnSync, only async */
exports.readln = async function utils$$rawReadln(echoChar, bufSize) {
  var byteBuf;                              /* io buffer */
  var buf = '';                             /* accumulator */
  var ret;
  var initRawMode = process.stdin.isRaw;
  var backup = require('./eventUtils').backupEvents(process.stdin, ['data', 'error', 'end']);
  var _resolve, _reject;
  
  if (process.stdin.setRawMode && !process.stdin.isRaw)
    process.stdin.setRawMode(true);

  function getData(data) {
    _resolve(data);
  }
  function trapEnd() {
    _resolve(-1);
  }
  function trapError(e) {
    _reject(e);
  }

  process.stdin.on('data',  getData);
  process.stdin.on('end',   trapEnd);
  process.stdin.on('error', trapError);

  try {
    loop: while (true) {
      if (!leftover) {
        byteBuf = await new Promise((resolve, reject) => {
          _resolve = resolve;
          _reject = reject;
        });
      } else {
        byteBuf = leftover;
        leftover = false;
      }

      ret = buildLine(buf, byteBuf, byteBuf.length, echoChar);
      buf = ret.buf;
      if (ret.done)
        break loop;
    }

    if (process.stdin.setRawMode) {
      process.stdout.write(Buffer.from([13]));
      process.stdout.write(Buffer.from([10]));
    }
  } finally {
    process.stdin.off('data',  getData);
    process.stdin.off('end',   trapEnd);
    process.stdin.off('error', trapError);
    require('./eventUtils').restoreEvents(backup, process.stdin);

    if (typeof initRawMode === 'boolean')
      process.stdin.setRawMode(initRawMode);
  }

  return buf;
}

function pythonmonkey$$readlnSync(prompt)
{
  globalThis.python.exec(`
def passphrase_prompt(prompt):
  import getpass
  passphrase = getpass.getpass(prompt)
  return passphrase
`);

  const result = globalThis.python.eval('passphrase_prompt')(prompt);
  return result;
}

/** Build up an input line input buffer at a time from a terminal in raw mode.
 *  @param   chbuf    {Buffer}  The input buffer
 *  @param   buf      {string}  The accumulated string in UTF-16
 *  @param   nBytes    {number}  The number of bytes in byteBuf
 *  @param   echoChar {buffer}  The character to echo back for each character-class codepoint
 *  @return  an object containing buf: a new accumulated string and done: a boolean that is true
 *           when we are finished processing the input stream.
 */
function buildLine(buf, byteBuf, nBytes, echoChar) {
  const os = require('os');
  var pos;
  var codepoint;
  
  for (pos=0; pos < nBytes; pos++) {
    if (byteBuf[pos] >= 0xc2)  /* start of utf-8 sequence */
      ({codepoint, pos} = consumeUtf8Sequence(byteBuf, pos));
    else
      codepoint = byteBuf[0];
    
    /* Emulate typical UNIX terminal line discipline, eg linux stty -iutf8 */
    switch(codepoint) {
    case 3:
      process.kill(process.pid, 'SIGINT');
      continue;
    case 8: case 127:
      if (echoChar) { /* "rub out" the deleted character */
        let width = (echoChar === true ? require('wcwidth')(buf[buf.length - 1]) : echoChar.length);
        for (let i=0; i < width; i++)
          process.stdout.write(Buffer.from([8,32,32,8,8]));
      }
      buf = buf.slice(0,-1);
      continue;
    case 4:
      if (!buf.length)
        return { done: true, buf: buf };
    case 10: case 13:
      if (nBytes - pos !== 0) {
        leftover = byteBuf.slice(pos + 1)
      }
      return { done: true, buf: buf };
    case 21:
      for (let i=0; i < buf.length; i++) {
        if (buf.charCodeAt(i) >= 0xd800 && buf.charCodeAt(i) < 0xdc00)
          continue;
        for (let j=0; j < (echoChar || '').length; j++)
          process.stdout.write(Buffer.from([8,32,8]));
      }
      buf = '';
      continue;
    case 26:
      if (os.platform !== 'win32')
        process.kill(process.pid, 'SIGTSTP');
      continue;
    case 28:
      process.kill(process.pid, 'SIGQUIT');
      continue;
    default:
      if (codepoint < 32)
        continue;
    }

    if (echoChar) {
      if (echoChar === true)
        process.stdout.write(Buffer.from(String.fromCodePoint(codepoint)));
      else
        process.stdout.write(Buffer.from(echoChar));
    }
    buf = (buf + String.fromCodePoint(codepoint)).normalize();
  }

  return { done: false, buf: buf };
}

/** Take a short nap - should cue the scheduler to take what's left of our timeslice. */
function nap() {
  require('./index').msleep(125/2);   /* 125ms = 100wpm */
}

/** Consume a utf8 sequence from the input buffer. If the input
 *  buffer runs out before the sequence is complete, stdin is
 *  read to finish it.  This code assumes that the input stream
 *  is valid utf8, and handles corrupted input streams poorly.
 *
 *  @param    byteBuf     input buffer filled by unsigned bytes
 *  @param    pos       current position in the input buffer: must point to
 *                      the first byte of a utf8 sequence.
 *
 *  @returns a non-negative integer that is the Unicode code point value
 */
function consumeUtf8Sequence(byteBuf, pos) {
  var utf8Buf;
  var nBytes;
  var ret = {};
  var missing;

  if (byteBuf[pos] < 0x80)
    utf8Buf = Buffer.alloc(1);
  else if (byteBuf[pos] >= 0xc2 && byteBuf[pos] <= 0xdf)
    utf8Buf = Buffer.alloc(2);
  else if (byteBuf[pos] >= 0xe0 && byteBuf[pos] <= 0xef)
    utf8Buf = Buffer.alloc(3);
  else if (byteBuf[pos] >= 0xf0 && byteBuf[pos] <= 0xf4) 
    utf8Buf = Buffer.alloc(4);
  else
    throw new Error(`invalid utf8 leading byte 0x${byteBuf[pos].toString(16)}`);
  
  nBytes = byteBuf.copy(utf8Buf, 0, pos, pos + utf8Buf.length);
  pos += nBytes;
  ret.pos = pos;

  for (let utf8Pos = utf8Buf.length - nBytes;
       utf8Pos < utf8Buf.length;
       utf8Pos += nBytes) {
    let nBytes = require('dcp/common/fs-basic').readSync(process.stdin.fd, utf8Buf, utf8Pos, utf8Buf.length - utf8Pos);
    if (nBytes < 0)
      throw new Error('Input stream closed while mid-utf8 sequence');
    if (nBytes === 0)
      nap();
  }

  ret.codepoint = utf8Buf.toString('utf8').codePointAt(0);
  return ret;
}

/** Get the leftover buffer from the last call to rawReadlnSync()
 *  @param     peek   {boolean}   If truey, leave leftovers in place for subsequent calls
 *                                to this function or rawReadlnSync().
 *  @returns  {object} buffer
 */ 
exports.getLeftOver = function utils$$getleftOver(peek) {
  let lo = leftover;

  if (!peek)
    leftover = false;

  return lo;
}

/** Prompt for a password, read the password, and resolve the returned
 *  promise with the password.
 *  @param   prompt    {string}    The string to print before waiting for the password
 *  @param   echoChar  {string}    The character to echo back, or undefined.
 *  @returns Promise
 *
 *  @todo Need to switch this to async code to prevent the event loop from blocking
 *        while the user is typing their password, but this is causing problems during
 *        package deployment, likely because of the fancy readline module in play in 
 *        that code. The tty is not in raw mode, but we're not getting data events. 
 *        The sync code is a lot more low-level and does not need to cooperate with
 *        the read stream. /wg may 2020
 */
exports.readPass = async function utils$$readPass(prompt, echoChar) {
  if (!fsBasic.stdout.isTTY)
    console.error("Passphrase prompt may be hidden because stdout is not tty.");

  if (require('dcp/common/dcp-env').platform === 'pythonmonkey')
    return pythonmonkey$$readlnSync(prompt);
  fsBasic.stdout.write(prompt);
  return exports.readlnSync(echoChar);
}

/** Prompt for a password, read the password, and return the password.
 *  Like readPass(), only fully synchronous.
 *  @param   prompt    {string}    The string to print before waiting for the password
 *  @param   echoChar  {string}    The character to echo back, or undefined.
 *  @returns password as string
 */
exports.readPassSync = function utils$$readPassSync(prompt, echoChar) {
  if (!fsBasic.stdout.isTTY)
    console.error("Passphrase prompt may be hidden because stdout is not tty.");

  if (require('dcp/common/dcp-env').platform === 'pythonmonkey')
    return pythonmonkey$$readlnSync(prompt);
  fsBasic.stdout.write(prompt);
  return exports.readlnSync(echoChar);
}
